import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { FadeUp } from "../../components/Reveal";

const STATUS = {
  baru: { label: "Baru", cls: "border-gold/50 text-gold" },
  diproses: { label: "Diproses", cls: "border-sky-400/50 text-sky-300" },
  selesai: { label: "Selesai", cls: "border-emerald-400/50 text-emerald-300" },
};
const NEXT = { baru: "diproses", diproses: "selesai" };

const Row = ({ item, onAdvance }) => {
  const [open, setOpen] = useState(false);
  const s = STATUS[item.status] || STATUS.baru;
  const next = NEXT[item.status];
  const tanggal = new Date(item.created_at).toLocaleString("id-ID", {
    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <div data-testid={`konsultasi-row-${item.id}`} className="border border-white/10 bg-[#0d0d0d]">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`konsultasi-toggle-${item.id}`}
        className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4 text-left"
      >
        <span className={`border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${s.cls}`}>
          {s.label}
        </span>
        <span className="font-serif text-lg text-[#F5F5F5]">{item.nama}</span>
        <span className="text-xs text-[#A3A3A3]">{item.layanan}</span>
        <span className="ml-auto flex items-center gap-2 text-xs text-[#737373]">
          {tanggal}
          <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="border-t border-white/10 px-5 py-4">
          <p className="text-sm font-light leading-relaxed text-[#C9C9C9]">{item.pesan}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-[#A3A3A3]">
            <span className="flex items-center gap-1.5"><Phone size={12} className="text-gold/70" /> {item.telepon}</span>
            {item.email && (
              <span className="flex items-center gap-1.5"><Mail size={12} className="text-gold/70" /> {item.email}</span>
            )}
            {next && (
              <button
                onClick={() => onAdvance(item.id, next)}
                data-testid={`konsultasi-advance-${item.id}`}
                className="ml-auto border border-gold/50 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-ink"
              >
                Tandai {STATUS[next].label}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminKonsultasi() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .get("/consultations")
      .then((r) => setItems(r.data))
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("ylbh_admin_token");
          navigate("/admin/login");
        } else {
          toast.error("Gagal memuat data konsultasi.");
          setItems([]);
        }
      });
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const advance = async (id, status) => {
    try {
      await api.patch(`/consultations/${id}/status`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Status diubah menjadi "${STATUS[status].label}".`);
    } catch {
      toast.error("Gagal mengubah status.");
    }
  };

  const count = (st) => (items || []).filter((x) => x.status === st).length;

  return (
    <AdminShell title="Dashboard Admin">
      <h1 className="font-serif text-4xl text-[#F5F5F5]">Permohonan Konsultasi</h1>

      <FadeUp className="mt-8 grid grid-cols-2 gap-px border border-white/10 bg-white/10 lg:grid-cols-4">
        {[
          ["Total", (items || []).length],
          ["Baru", count("baru")],
          ["Diproses", count("diproses")],
          ["Selesai", count("selesai")],
        ].map(([label, val], i) => (
          <div key={label} data-testid={`stat-${label.toLowerCase()}`} className="bg-[#0A0A0A] p-6">
            <p className="font-serif text-3xl text-gold">{val}</p>
            <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.25em] text-[#A3A3A3]">{label}</p>
          </div>
        ))}
      </FadeUp>

      <div className="mt-10 space-y-3">
        {items === null ? (
          <p className="py-16 text-center font-serif text-xl italic text-[#737373]">Memuat…</p>
        ) : items.length === 0 ? (
          <p data-testid="konsultasi-empty" className="border border-white/10 bg-[#0d0d0d] py-16 text-center font-serif text-xl italic text-[#737373]">
            Belum ada permohonan konsultasi masuk.
          </p>
        ) : (
          items.map((item) => <Row key={item.id} item={item} onAdvance={advance} />)
        )}
      </div>
    </AdminShell>
  );
}
